<template>
  <div class="contacts-info-block__wrapper">
    <div class="contacts-info-block__content">
      <div class="contacts-info-block__main-info">
        <VForm
          :name="name"
          :initialValues="{
            first_name: firstName,
            last_name: lastName,
            email,
            phone,
            company
          }"
          :fields="fields"
          @change="emitedObj => updateField(emitedObj.name, emitedObj.value)"
          extraClass="contactInfoBlock"
        ></VForm>
        <VAddressEditor
          :initialValues="{ street, unit, country, city, state, zip }"
          @save="val => updateField('address', val)"
        />

        <div class="item tags" :class="{ ['empty']: !tagsEmpty }">
          <span class="title">
            <img :src="tagsEmpty ? checkIconActive : checkIcon" />Tags
          </span>

          <VInlineTagsCreator
            :tagsList="tags"
            minItemsCount="1"
            extraClass="contacts-tags-inline"
            @statusChange="updateTagsStatus"
            @select="onTagSelect"
            @remove="onTagRemove"
          />
        </div>

        <div class="expandable" v-show="expanded">
          <div class="item">
            <span class="title"> Created </span
            ><span class="value">{{ formatDate(created) }} </span>
          </div>

          <div class="item">
            <span class="title"> Modified </span>
            <span class="value">{{ formatDate(modified) }} </span>
          </div>

          <div class="item">
            <span class="title"> Source </span>
            <span class="value">{{ _processMailPieceSource(source) }} </span>
          </div>

          <div class="item id">
            <span class="title"> ID: </span>
            <span class="value id" :title="id">{{ id }} </span>
            <i class="fal fa-copy copy-id" @click="copy(id)"></i>
          </div>
        </div>

        <div class="expand" @click="toggleExpand">
          Show {{ expanded ? "less" : "4 more" }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import moment from "moment";
import { mapActions, mapGetters } from "vuex";

// components
import VHr from "@/components/VHr";
import VTag from "@/components/VTag";
import VEditableText from "@/components/VEditableText";
import VDropDownSelect from "@/components/VDropDownSelect";
import VInlineTagsCreator from "@/components/VInlineTagsCreator";
import VAddressEditor from "@/components/VAddressEditor";
import VForm from "@/components/VForm";

// mixins
import withCopy from "@/mixins/withCopy";
import withToasted from "@/mixins/withToasted";
import withExpand from "@/mixins/withExpand";
import withFormUtils from "@/mixins/withFormUtils";
import withValidation from "@/mixins/withValidation";

//utils
import {
  required,
  email,
  phone,
  maxLength,
  maxLengthSplit
} from "@/utils/validations";

import {
  capitalizeFirstLetter,
  convertTagsToSelectListFormat,
  processMailpieceSource
} from "@/utils/util";

import inkLogo from "@/assets/images/app_logo.svg";
import checkIcon from "@/assets/images/checkmark.svg";
import checkIconActive from "@/assets/images/checkmark-checked.svg";

export default Vue.component("ContactsInfoBlock", {
  props: {
    id: VueTypes.string.isRequired,
    firstName: VueTypes.string.isRequired,
    lastName: VueTypes.string.isRequired,
    street: VueTypes.string.isRequired,
    email: VueTypes.string,
    zip: VueTypes.string.isRequired,
    country: VueTypes.string.isRequired,
    state: VueTypes.string.isRequired,
    city: VueTypes.string.isRequired,
    tags: VueTypes.any,
    customData: VueTypes.array,
    created: VueTypes.any.isRequired,
    modified: VueTypes.any,
    source: VueTypes.string.isRequired,
    phone: VueTypes.string,
    deliverability: VueTypes.string,

    company: VueTypes.string,
    unit: VueTypes.string,
    image: VueTypes.string.def("")
  },

  components: {
    VInlineTagsCreator,
    VAddressEditor,
    VForm
  },

  mixins: [withCopy, withToasted, withExpand, withFormUtils, withValidation],

  data() {
    return {
      name: "contactInfo",
      inkLogo,
      checkIcon,
      checkIconActive,
      tagsEmpty: !!this.tags.length,
    };
  },

  methods: {
    _processMailPieceSource(str) {
      return processMailpieceSource(str);
    },

    updateTagsStatus(val) {
      this.tagsEmpty = val;
    },

    getLogoLetters() {
      return `${this.firstName[0]}${this.lastName[0]}`;
    },

    formatDate(date) {
      return moment(date).format("MMM D, YYYY z");
    },

    updateField(key, value) {
      //check is field exist or is errors in field
      if (!this.getField(key) || !this.getField(key).meta.errors.length) {
        let data = { id: this.id, source: "manual" };

        if (key === "address") {
          let { unit, street, state, zip, country, city } = value;

          data = {
            ...data,
            address_line_1: street,
            address_line_2: unit,
            address_state: state,
            address_zip: zip,
            address_country: country,
            address_city: city
          };
        } else {
          data = { ...data, [key]: value.trim() };
        }

        this.editContact({
          data,
          onSuccess: contact => {
            if (key === "address") {
              this.validateContact(contact);
            }

            this.toast("The field has been updated.");
          },

          onError: error => {
            let message = error;

            if (typeof error === "object") {
              if (error.description) {
                message = capitalizeFirstLetter(error.description);
              } else {
                message = "Something went wrong.";
              }
            }

            this.toast(message, "error");
          }
        });
      }
    },

    _saveTags(tags) {
      this.editContact({
        data: {
          id: this.id,
          tags: tags,
          source: "manual"
        },
        onSuccess: () => {
          this.toast("The tags field has been updated.", "success");
        },

        onError: error => {
          this.toast(error, "error");
        }
      });
    },

    onTagSelect({ key, value, label }) {
      if (value.includes("#new-")) {
        const name = value.split("#new-")[1];
        this.createTag({
          payload: { name },
          onSuccess: tag => {
            const ids = this.tags.map(item => item.id);
            this._saveTags([...ids, tag.id]);
          }
        });
      } else {
        const ids = this.tags.map(item => item.id);
        this._saveTags([...ids, key]);
      }
    },

    onTagRemove(data) {
      const ids = this.tags.map(item => item.id);
      const idsToSave = ids.filter(item => item !== data.key);
      this._saveTags(idsToSave);
    },

    // maxLengthFirstLastNames() {
    //   const { first_name, last_name } = this.formValues;
    //   if (first_name && last_name) {
    //     const firstNameField = this.getField("first_name");
    //     const lastNameField = this.getField("last_name");
    //     if (firstNameField.meta.blurred && lastNameField.meta.blurred) {
    //       return maxLengthSplit(40)(first_name, last_name);
    //     }
    //   }
    // },

    ...mapActions("contacts", ["editContact", "createTag", "validateContact"])
  },

  computed: {
    fields() {
      return [
        {
          name: "first_name",
          elementType: "editableText",
          placeholder: "Enter First Name",
          label: "First Name",
          validate: [
            required,
            this.validatePairStringLength(40, "first_name", "last_name")
          ]
        },
        {
          name: "last_name",
          elementType: "editableText",
          placeholder: "Enter Last Name",
          label: "Last Name",
          validate: [
            required,
            this.validatePairStringLength(40, "first_name", "last_name")
          ]
        },
        {
          name: "email",
          elementType: "editableText",
          label: "Email",
          placeholder: "Enter Email",
          validate: [email]
        },
        {
          name: "phone",
          elementType: "editableText",
          label: "Phone",
          placeholder: "e.g. +1 201-555-0123",
        },
        {
          name: "company",
          elementType: "editableText",
          label: "Company",
          placeholder: "e.g. SpaceX",
          validate: [maxLength(40)]
        }
      ];
    },
    _tags() {
      return convertTagsToSelectListFormat(this.storeTags);
    },

    ...mapGetters("contacts", { storeTags: "tags" })
  }
});
</script>

<style lang="scss" scoped>
$ns: "contacts-info-block";

.#{$ns} {
  &__wrapper {
    border-radius: 4px;
    height: 100%;

    .expand {
      font-size: 15px;
      color: #acacac;
      cursor: pointer;
      margin-top: 20px;
      font-family: "Lato-Medium", Arial, sans-serif;
      font-weight: 500;
      transition: color 0.1s linear;

      &.no-tags {
        margin-top: 0;
      }

      &:hover {
        color: $primary-color;
      }
    }
  }

  &__heading {
    margin-top: 50px;
    text-align: center;
    font-size: 25px;

    .company {
      @include textCropEllipsis();
      font-size: 15px;
      text-align: center;
      margin-top: 10px;
      font-style: italic;
    }
  }

  &__main-info {
    .dropdown-select__outer {
      /*margin-top: 10px;*/
    }

    &.expandable {
      margin-top: 13px;
    }

    .item {
      @include textCropEllipsis();
      color: $secondary-text-color;
      font-size: 14px;
      display: flex;
      align-items: center;
      height: 27px;
      padding-left: 20px;
      margin-bottom: 10px;

      .title {
        display: inline-block;
        color: $default-color;
        margin-right: 7px;
        width: 72px;
        min-width: 72px;
      }

      &.tags {
        margin-top: 10px;
        height: auto;
        align-items: flex-start;
        padding-left: 0;
        overflow: visible;

        img {
          margin-right: 8px;
        }

        .title {
          display: flex;
          width: 91px;
          min-width: 91px;
          margin-top: 3px;
          margin-right: 10px;

          /*@media screen and (min-width: 991px) {*/
          /*margin-right: 10px;*/
          /*width: auto;*/
          /*min-width: auto;*/
          /*}*/
        }

        &.empty {
          .title {
            margin-top: 0;
          }
        }
      }

      &:last-child {
        margin-bottom: 0;
      }

      .value {
        @include textCropEllipsis();
        width: 100%;
        padding: 3px;

        &.address {
          span {
            margin-right: 3px;
          }
        }

        &.id {
          /*width: 61px;*/
        }
      }

      &.address .value {
        white-space: normal;
      }

      &.id {
        i {
          margin-left: 5px;
          cursor: pointer;

          &:hover {
            color: $primary-color;
          }
        }
      }
    }
  }

  &__title-wrapper {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
  }
}

.contactInfoBlock /deep/ .field-wrapper {
  margin-bottom: 0;
}

/deep/ .contacts-tags-inline {
  max-width: 65%;

  li {
    @include textCropEllipsis();
  }
}
</style>
